<template>
  <div id="app">
    <!-- Popup publicitario (Desactivado por fin de campaña) -->
    <AdsModalComp v-if="false" />    
    <SessionExpiredModalComp />
    <LoadingIndicator v-if="isLoading" />
    <!-- 1. Capa de Fondo: Solo la imagen, en la capa más baja. -->
    <section v-if="hasHero" class="hero-background" :style="heroStyle">
      <!-- 2. Contenido del Hero: Ahora anidado para permitir flujo relativo en móvil -->
      <div class="hero-slot-container" :style="heroSlotContainerStyle">
        <div id="hero-slot"></div>
      </div>
    </section>

    <!-- 3. Header Unificado: Se renderiza sobre todo lo demás. -->
    <HeaderComp :transparent="hasHero" />

    <!-- 4. Sidebar: Ahora es un elemento de nivel superior para un z-index correcto -->
    <SidebarComp :class="{ 'sidebar-hidden': isMobile }" />

    <div class="layout">
      <div class="main-content">
        <div class="page-body">
          <main>
            <!-- El router-view necesita un padding superior para no quedar detrás del hero -->
            <router-view :style="routerViewStyle" />
          </main>
        </div>
      </div>
    </div>

    <FooterComp />
    <CircularHelpMenuComp />
  </div>
</template>

<script>
import AdsModalComp from "./components/AdsModalComp.vue";
import SidebarComp from "./components/SidebarComp.vue";
import HeaderComp from "./components/HeaderComp.vue";
import CircularHelpMenuComp from "./components/CircularHelpMenuComp.vue";
import FooterComp from "./components/FooterComp.vue";
import LoadingIndicator from "./components/LoadingIndicator.vue";
import SessionExpiredModalComp from "./components/SessionExpiredModalComp.vue"; // Importamos el nuevo modal
import { initializeHeartbeat, cleanupHeartbeat } from "@/services/heartbeat.js";
import { loadingStore } from "./stores/loadingstore.js"; // Este import ya es correcto

export default {
  name: "App",
  components: {
    AdsModalComp,
    SidebarComp,
    HeaderComp,
    CircularHelpMenuComp,
    FooterComp,
    LoadingIndicator,
    SessionExpiredModalComp, // Registramos el nuevo modal
  },
  data() {
    return {
      isMounted: false, // Nueva propiedad para controlar el montaje
      isMobile: window.innerWidth <= 1024,
    };
  },
  computed: {
    routeName() {
      return this.$route?.name || "";
    },
    // Comprobación de ruta insensible a mayúsculas para robustez
    currentRouteNameLower() {
      return this.routeName.toLowerCase();
    },
    isLoading() {
      return loadingStore.isLoading;
    },
    heroImage() {
      switch (this.currentRouteNameLower) {
        case "flights":
        case "flightresults": // Corregido para coincidir con 'FlightResults'
        case "checkout":
        case "checkoutlanding":
        case "checkoutlandingpayment":
        case "login":
        case "register":
        case "quotetouristplan":
        case "requestcontactme":
        case "reservationssummary":
          return this.isMobile
            ? require("@/assets/images/vuelos-600.webp")
            : require("@/assets/images/vuelos-1920.webp");
        case "hotels":
        case "hotelresults":
        case "hoteldetails": // <-- AÑADIDO: Para que la página de detalles tenga imagen
          return this.isMobile
            ? require("@/assets/images/hoteles-600.webp")
            : require("@/assets/images/hoteles-1920.webp");
        case "cars":
        case "carresults":
          return this.isMobile
            ? require("@/assets/images/autos-600.webp")
            : require("@/assets/images/autos-1920.webp");
        case "transfers":
        case "transferresults":
          return this.isMobile
            ? require("@/assets/images/traslados-600.webp")
            : require("@/assets/images/traslados-1920.webp");
        case "lounges":
        case "loungeresults":
          return this.isMobile
            ? require("@/assets/images/salas-600.webp")
            : require("@/assets/images/salas-1920.webp");
        case "assists":
        case "assistresults":
          return this.isMobile
            ? require("@/assets/images/asistencia-600.webp")
            : require("@/assets/images/asistencia-1920.webp");
        case "shows":
        case "showresults":
        case "showdetails": // <-- AÑADIDO: Para que la página de detalles tenga imagen
          return this.isMobile
            ? require("@/assets/images/espectaculos-600.webp")
            : require("@/assets/images/espectaculos-1920.webp");            
        case "home":
          return require("@/assets/images/home-background.webp");
        case "error": // <-- ¡AQUÍ ESTÁ LA SOLUCIÓN!
          return require("@/assets/images/home-background.webp");
        default:
          return "";
      }
    },
    hasHero() {
      return !!this.heroImage;
    },
    heroHeight() {
      const routeName = this.currentRouteNameLower;
      if (
        routeName === "checkout" ||
        routeName === "hoteldetails" ||
        routeName === "checkoutlanding" ||
        routeName === "checkoutlandingpayment" ||
        routeName === "login" ||
        routeName === "register" ||
        routeName === "quotetouristplan" ||
        routeName === "requestcontactme" ||
        routeName === "reservationssummary" ||
        routeName === "showdetails"
      ) {
        return "30vh"; // Altura más reducida para checkout
      }
      // <-- MODIFICADO: Agrupamos resultados y detalles
      if (
        routeName === "flightresults" ||
        routeName === "hotelresults" ||
        routeName === "carresults" ||
        routeName === "transferresults" ||
        routeName === "loungeresults" ||
        routeName === "assistresults" ||
        routeName === "showresults"
      ) {
        return "30vh"; // Altura para resultados
      }
      if (
        routeName === "flights" ||
        routeName === "hotels" ||
        routeName === "cars" ||
        routeName === "transfers" ||
        routeName === "lounges" ||
        routeName === "assists" ||
        routeName === "shows"
      ) {
        // ¡Aquí está la solución!
        // En móvil la altura es automática (min-height 100vh), aquí definimos solo desktop.
        return "50vh";
      }
      // Altura por defecto para home, flights, hotels, etc.
      return "50vh";
    },
    heroStyle() {
      if (!this.hasHero) return {};
      const baseStyle = {
        backgroundImage: `url(${this.heroImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      };

      if (this.isMobile) {
        // Lista de rutas que deben ocupar toda la pantalla en móvil (Buscadores principales)
        const fullScreenRoutes = [
          "home",
          "flights",
          "hotels",
          "cars",
          "transfers",
          "lounges",
          "assists",
          "shows",
          "error",
        ];

        if (fullScreenRoutes.includes(this.currentRouteNameLower)) {
          return {
            ...baseStyle,
            position: "relative",
            minHeight: "100vh",
            height: "auto",
            zIndex: 1,
          };
        }

        // Para resultados y checkout, usamos la altura definida en heroHeight (30vh o 40vh)
        // pero mantenemos position: relative para que empuje el contenido hacia abajo.
        return {
          ...baseStyle,
          position: "relative",
          minHeight: this.heroHeight,
          height: "auto",
          zIndex: 1,
        };
      }

      return {
        ...baseStyle,
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: this.heroHeight,
        zIndex: 1,
      };
    },
    heroSlotContainerStyle() {
      if (this.isMobile) {
        return {
          position: "relative",
          height: "auto",
          paddingTop: "100px", // Espacio para el header
          paddingBottom: "50px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "center",
        };
      }
      return {
        // En desktop, confiamos en las clases CSS (position: absolute) y height 100%
        height: "100%",
      };
    },
    routerViewStyle() {
      if (this.hasHero && !this.isMobile) {
        // Solo en desktop necesitamos padding, en móvil el flujo es natural (relative)
        return { paddingTop: this.heroHeight };
      }
      return {};
    },
  },
  methods: {
    handleResize() {
      this.isMobile = window.innerWidth <= 1024;
    },
  },
  mounted() {
    window.addEventListener("resize", this.handleResize);
    this.isMounted = true; // El componente App ya está en el DOM
    initializeHeartbeat(); // Inicia el sistema de detección de inactividad.
  },
  beforeUnmount() {
    // Es importante eliminar el listener para evitar fugas de memoria
    // al cambiar de componente (aunque en App.vue es menos crítico).
    window.removeEventListener("resize", this.handleResize);
    cleanupHeartbeat(); // Limpia los listeners y el temporizador de inactividad.
  },
};
</script>

<style>
:root {
  --color-acento-primario: #00bdd6; /*Color de la mayoria de botones y lineas*/
  --color-acento-primario-suave: #00a9bf; /*Sombra de la mayoria de botones y lineas*/
  --color-acento-secundario: #1a4a8c; /*Color de los textos principales*/
  --color-acento-secundario-suave: #6a8db3; /*Color suave de los textos principales*/
  --color-acento-terciario: #1a4a8c; /*Color de las barras laterales y algunos botones*/
  --color-acento-terciario-transparente: #1a4a8cd9; /*Versión transparente para fondos y hover*/
  --color-acento-terciario-bordes: #66afe9; /*Color para bordes y líneas secundarias*/
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

/* --- ESTILOS GLOBALES DE FUENTE Y COLOR --- */

/* 
  --- IMPORTACIÓN DE FUENTES LOCALES ---
  Aquí le decimos al navegador dónde encontrar los archivos de la fuente.
  Asegúrate de que la ruta en `url()` sea correcta y que los archivos
  de la fuente (ej. .woff, .woff2) estén en esa ubicación.
*/
@font-face {
  font-family: "Blogger Sans";
  src: url("~@/assets/fonts/BloggerSans/fonts/BloggerSans.woff") format("woff");
  font-weight: normal;
  font-style: normal;
  font-display: swap;
}

@font-face {
  font-family: "Blogger Sans";
  src: url("~@/assets/fonts/Blogger-Sans-Bold/fonts/Blogger Sans-Bold.woff")
    format("woff");
  font-weight: bold;
  font-style: normal;
  font-display: swap;
}

@font-face {
  font-family: "Blogger Sans";
  src: url("~@/assets/fonts/Blogger-Sans-Light/fonts/Blogger Sans-Light.woff")
    format("woff");
  font-weight: 300;
  font-style: normal;
  font-display: swap;
}

@font-face {
  font-family: "Blogger Sans";
  src: url("~@/assets/fonts/Blogger-Sans-Medium/fonts/Blogger Sans-Medium.woff")
    format("woff");
  font-weight: 500;
  font-style: normal;
  font-display: swap;
}

body {
  /* 3. Usamos el nombre limpio que definimos en @font-face. */
  font-family: "Blogger Sans", sans-serif;
  color: var(--color-acento-secundario);
  background-color: #f4f7f9; /* Color de fondo base para toda la app */
  -webkit-font-smoothing: antialiased; /* Mejora el renderizado de fuentes en WebKit */
  -moz-osx-font-smoothing: grayscale; /* Mejora el renderizado de fuentes en Firefox */
}

/* Forzar herencia de fuente en elementos de formulario */
input,
button,
select,
textarea {
  font-family: inherit;
}

#app {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  position: relative; /* Contexto para elementos posicionados absolutamente */
}

/* === ARQUITECTURA DE CAPAS  === */

/* Capa 0: Fondo */
.hero-background {
  /* position: absolute; Sobrescrito por inline style según el dispositivo */
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 1;
  background-repeat: no-repeat;
}

/* Capa 2: Contenido del Hero (Buscador/Resumen) */
.hero-slot-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 2; /* Por encima del contenido principal para ser interactivo */
  display: flex;
  justify-content: center;
  align-items: flex-end; /* Alinea el contenido abajo por defecto */
}

#hero-slot {
  max-width: 1200px;
  width: 100%;
  margin-bottom: 50px;
  margin-left: 5%;
}

/* Capa 1: Layout Principal */
.layout {
  display: flex;
  flex-grow: 1;
  flex-direction: row;
}

.main-content {
  flex-grow: 1;
  /* El margen solo se aplica en escritorio para dejar espacio a la sidebar */
  margin-left: 0;
  display: flex;
  flex-direction: column;
}

.page-body,
main {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

main {
  margin-left: 5%;
}

/* Footer */
footer {
  background-color: #222;
  color: #ccc;
  text-align: center;
  padding: 1rem;
  width: 100%;
  position: relative;
  z-index: 1;
}

/* --- Responsive Design --- */
@media (max-width: 1024px) {
  .main-content {
    margin-left: 0; /* Eliminamos el margen de la sidebar en móvil */
  }
  /* .hero-slot-container {
    align-items: flex-start;
    padding-top: 100px;
  } */ /* Se maneja ahora en computed style */

  #hero-slot {
    margin-left: 0;
  }

  main {
    margin-left: 0;
  }
}
</style>
